import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GoogleLogin } from "@react-oauth/google";
import { useAuthStore } from "../store/auth";
import { useCartStore } from "../store/cart";
import api from "../lib/api";
import toast from "react-hot-toast";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [marketingEmails, setMarketingEmails] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const { fetch: fetchCart } = useCartStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!privacyConsent) {
      toast.error(t("gdpr.privacyPolicyConsent"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { 
        name, 
        email, 
        password,
        marketingEmails
      });
      setAuth(data.token, data.user);
      await fetchCart();
      toast.success(t("auth.accountCreated"), { duration: 6000 });
      navigate("/");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Registration failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSuccess(credentialResponse: any) {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google", {
        token: credentialResponse.credential,
      });
      setAuth(data.token, data.user);
      await fetchCart();
      if (data.isNewUser) {
        navigate("/onboarding", { state: { from: "/" }, replace: true });
      } else {
        toast.success(t("auth.welcomeBack", { name: data.user.name }));
        navigate("/");
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Google Sign-In failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-bold text-gray-900 mb-2">
            {t("auth.registerTitle")}
          </h1>
          <p className="text-gray-500 text-sm">{t("auth.registerSub")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t("auth.fullName")}
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="Jane Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t("auth.email")}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t("auth.password")}{" "}
              <span className="text-gray-400 font-normal">{t("auth.passwordHint")}</span>
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="••••••••"
            />
          </div>

          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                required
                checked={privacyConsent}
                onChange={(e) => setPrivacyConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
              />
              <span className="text-xs text-gray-500 leading-normal group-hover:text-gray-700 transition-colors">
                {t("gdpr.privacyPolicyConsent")}
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={marketingEmails}
                onChange={(e) => setMarketingEmails(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
              />
              <span className="text-xs text-gray-500 leading-normal group-hover:text-gray-700 transition-colors">
                {t("gdpr.marketingConsent")}
              </span>
            </label>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? t("auth.creating") : t("auth.create")}
          </button>
        </form>

        {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
          <>
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300"></span>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t("common.or") || "Or"}</span>
              </div>
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error("Google Sign-In failed")}
                useOneTap
                width="100%"
                theme="outline"
              />
            </div>
          </>
        )}

        <p className="text-center text-xs text-gray-400 mt-4">
          {t("auth.terms1")}{" "}
          <Link to="/terms" className="text-brand-600 hover:underline">{t("auth.termsLink")}</Link>{" "}
          {t("auth.and")}{" "}
          <Link to="/privacy" className="text-brand-600 hover:underline">{t("auth.privacyLink")}</Link>.
        </p>

        <p className="text-center text-sm text-gray-500 mt-4">
          {t("auth.haveAccount")}{" "}
          <Link to="/login" className="font-semibold text-brand-600 hover:text-brand-700">
            {t("auth.signIn")}
          </Link>
        </p>
      </div>
    </main>
  );
}
